import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import H2 from 'UIComponents/elements/headings/H2';
import UIButton from 'UIComponents/button/UIButton';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIDialogFooter from 'UIComponents/dialog/UIDialogFooter';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import UIModal from 'UIComponents/dialog/UIModal';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import useFetchFolderHierarchy from 'framework-listing-lib/internal/Folders/hooks/useFetchFolderHierarchy';
import useFolderActions from 'framework-listing-lib/internal/Folders/hooks/useFolderActions';
import { UNFILED_ASSETS_ID, FOLDER_PROPERTIES } from 'framework-listing-lib/internal/Folders/constants/folders';
import { getPropertyValue } from 'framework-listing-lib/utils/crmObject';
import useCrmObjectTypeName from 'framework-listing-lib/internal/hooks/useCrmObjectTypeName';
import usePreviousFolderId from '../internal/Folders/hooks/usePreviousFolderId';
import useGetInitialFolderName from 'framework-listing-lib/internal/Folders/hooks/useGetInitialFolderName';
import { useIsFoldersMode } from 'framework-listing-lib/internal/hooks/useUIMode';
import { getSelectedAssetFolderId } from 'framework-listing-lib/internal/Dialog/utils/dialog';
import ListingLibMoveAssetsModalBody from 'framework-listing-lib/internal/Folders/components/ListingLibMoveAssetsModalBody';

/**
 * This modal is being used in a details page by automation team
 * Although this is not a use case we're aiming to support, this was the fastest way
 * for them to rebuild their old move to folder action.
 *
 * `onSuccess` and `onError` callbacks were added for this purpose.
 */
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export default function ListingLibMoveAssetsModal({
  onClose,
  selectedObjectOrObjects,
  selectedObjectsCount,
  onSuccess,
  onError,
  selectionMode
}) {
  const isFoldersMode = useIsFoldersMode();
  const selectedAssetFolderId = useMemo(() => {
    if (!isFoldersMode) {
      return UNFILED_ASSETS_ID.toString();
    }
    return getSelectedAssetFolderId(selectedObjectOrObjects);
  }, [isFoldersMode, selectedObjectOrObjects]);
  const [currentFolderId, setCurrentFolderId] = useState(parseInt(selectedAssetFolderId, 10));
  const [currentFolderName, setCurrentFolderName] = useState('');
  const [isMovingToARecentFolder, setIsMovingToARecentFolder] = useState(false);
  const {
    previousFolderId,
    hasPreviousFolderId
  } = usePreviousFolderId(selectedObjectOrObjects);
  const {
    onClickFolder,
    openFoldersMap
  } = useFetchFolderHierarchy(currentFolderId);
  const {
    error,
    loading,
    moveAssetToFolder
  } = useFolderActions();
  const {
    objectTypeNamePlural,
    objectTypeNameSingular
  } = useCrmObjectTypeName();
  const {
    assetCount,
    assetName
  } = useMemo(() => {
    if (selectedObjectsCount && selectedObjectsCount > 1) {
      return {
        assetCount: selectedObjectsCount
      };
    }
    if (!Array.isArray(selectedObjectOrObjects)) {
      return {
        assetCount: 1,
        assetName: selectedObjectOrObjects.properties['hs_name'].value
      };
    }
    if (selectedObjectOrObjects.length === 1) {
      return {
        assetCount: 1,
        assetName: selectedObjectOrObjects[0].properties['hs_name'].value
      };
    }
    return {
      assetCount: selectedObjectOrObjects.length
    };
  }, [selectedObjectOrObjects, selectedObjectsCount]);
  const handleOpenChange = useCallback(folder => {
    setCurrentFolderId(folder.objectId);
    setCurrentFolderName(getPropertyValue(folder, FOLDER_PROPERTIES.name) || null);
    onClickFolder(folder);
    setIsMovingToARecentFolder(false);
  }, [onClickFolder]);
  const handleClickRecentFolder = useCallback(folder => {
    setCurrentFolderId(folder.objectId);
    setCurrentFolderName(getPropertyValue(folder, FOLDER_PROPERTIES.name) || null);
    setIsMovingToARecentFolder(true);
  }, []);
  const handleMoveAssetsToFolder = useCallback(() => {
    const assetIds = Array.isArray(selectedObjectOrObjects) ? selectedObjectOrObjects.map(crmObject => crmObject.objectId) : [selectedObjectOrObjects.objectId];
    moveAssetToFolder({
      assetIds,
      isRecentFolder: isMovingToARecentFolder,
      newFolderId: currentFolderId || UNFILED_ASSETS_ID,
      openFoldersMap,
      previousFolderId: previousFolderId || UNFILED_ASSETS_ID,
      selectedObjectsCount,
      selectionMode
    }).then(() => {
      if (onSuccess) {
        onSuccess({
          previousFolderId: previousFolderId || UNFILED_ASSETS_ID,
          currentFolderId: currentFolderId || UNFILED_ASSETS_ID,
          currentFolderName
        });
      }
      onClose();
    }).catch(err => {
      if (onError) {
        onError(err);
      }
      // Eslint catch statement
    });
  }, [currentFolderId, currentFolderName, isMovingToARecentFolder, moveAssetToFolder, onClose, onError, onSuccess, openFoldersMap, previousFolderId, selectedObjectOrObjects, selectedObjectsCount, selectionMode]);

  /**
   * We only want to set the current folder name in the move asset modal once.
   */
  const hasAlreadySetInitialName = useRef(false);
  const initialFolderName = useGetInitialFolderName({
    folderId: parseInt(selectedAssetFolderId, 10),
    openFoldersMap,
    skip: hasAlreadySetInitialName.current
  });
  useEffect(() => {
    if (hasAlreadySetInitialName.current || !initialFolderName) {
      return;
    }
    setCurrentFolderName(initialFolderName);
    hasAlreadySetInitialName.current = true;
  }, [initialFolderName]);
  return /*#__PURE__*/_jsxs(UIModal, {
    "data-test-id": "crm-listing-move-assets-modal",
    "data-test-asset-count": assetCount,
    width: 600,
    children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
      children: [/*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "frameworkListingLib.folders.moveAssetsModal.title"
        })
      }), /*#__PURE__*/_jsx(UIDialogCloseButton, {
        onClick: onClose
      })]
    }), /*#__PURE__*/_jsx(UIDialogBody, {
      children: /*#__PURE__*/_jsx(ListingLibMoveAssetsModalBody, {
        assetCount: assetCount,
        assetName: assetName,
        currentFolderId: currentFolderId,
        currentFolderName: currentFolderName,
        error: error,
        onClickRecentFolder: handleClickRecentFolder,
        onOpenChange: handleOpenChange,
        openFoldersMap: openFoldersMap,
        selectedAssetFolderId: selectedAssetFolderId,
        selectedObjectOrObjects: selectedObjectOrObjects,
        selectedObjectsCount: selectedObjectsCount
      })
    }), /*#__PURE__*/_jsxs(UIDialogFooter, {
      children: [/*#__PURE__*/_jsx(UITooltip, {
        disabled: !hasPreviousFolderId || currentFolderId !== previousFolderId,
        title: I18n.text('frameworkListingLib.folders.moveAssetsModal.footer.moveTooltip', {
          count: assetCount,
          objectTypeName: objectTypeNameSingular,
          objectTypeNamePlural
        }),
        children: /*#__PURE__*/_jsx(UILoadingButton, {
          "data-test-id": "move-to-folder-confirm-button",
          loading: loading,
          use: "primary",
          onClick: handleMoveAssetsToFolder,
          disabled: loading || hasPreviousFolderId && currentFolderId === previousFolderId,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "frameworkListingLib.folders.moveAssetsModal.footer.move"
          })
        })
      }), /*#__PURE__*/_jsx(UIButton, {
        onClick: onClose,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "frameworkListingLib.folders.moveAssetsModal.footer.cancel"
        })
      })]
    })]
  });
}